import { useCallback, useEffect, useMemo, useState } from 'react';

import { SpaceHierarchyReadModel } from '@client/api';
import { NodeModel } from '@minoru/react-dnd-treeview';

export type SpaceNodeData = {
  item: SpaceHierarchyReadModel;
  parent?: SpaceHierarchyReadModel;
}

/**
 * Recursively parsing each item inside of the passed `item` into an array of `NodeModel<SpaceNodeData>`
 * @param item A `SpaceHierarchyReadModel` item that needs to be parsed into a `NodeModel<SpaceNodeData>`
 * @param parent An optional parent which the passed `item` belongs to (for the root of the hierarchy this will be `undefined`)
 * @returns
 */
function parseReadModelToNodeModel(item: SpaceHierarchyReadModel, parent?: SpaceHierarchyReadModel): NodeModel<SpaceNodeData>[] {
  const { children, ...itemWithoutChildren } = item;
  let parentWithoutChildren;

  if (parent) {
    const { children: _, ...x } = parent || {};
    parentWithoutChildren = x;
  }

  let treeData: NodeModel<SpaceNodeData>[] = [{
    id: item.id,
    parent: parent?.id || 'root',
    text: item.name,
    droppable: true,
    data: {
      item: { ...itemWithoutChildren, children: [] }, // clearing out children because they wont be needed (unnecessary data)
      parent: parentWithoutChildren ? { ...parentWithoutChildren, children: [] } : undefined, // clearing out children because they wont be needed (unnecessary data)
    }
  }];

  if (Array.isArray(children) && children.length > 0) {
    for (const child of children) {
      treeData = treeData.concat(parseReadModelToNodeModel(child, item));
    }
  }

  return treeData;
}

/**
 * Will update the `data` property for each node e.g. whenever the tree changed
 * @param tree Array of node data that needs to have their `data` prop updated
 * @returns Updated tree with all nodes and their `data` prop updated
 */
function updateDataProperty(tree: NodeModel<SpaceNodeData>[]): NodeModel<SpaceNodeData>[] {
  const newTree = [...tree];
  for (let node of newTree) {
    const newNode = { ...node };
    // check if node has a new parent
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (newNode.data as any as SpaceNodeData).parent = newTree.find((x) => x.id === node.parent)?.data?.item;
    node = newNode;
  }
  return newTree;
}

/**
 * Checks if a target node is the descendant of a specified ancestor
 * @param tree Tree data that will be used for this operation
 * @param ancestorId The id of the ancestor the target
 * @param targetId The id of the target node (descendant) that needs to check if it has a specified ancestor
 * @returns True if the target node is a descendant of the ancestor - false if its not
 */
function isNodeAncestorOf(tree: NodeModel<SpaceNodeData>[], ancestorId: string | number, targetId: string | number): boolean {
  if (ancestorId === targetId) {
    return true;
  }

  const node = tree.find((x) => x.id === targetId);

  if (node == null) {
    return false;
  }

  if (node.parent === ancestorId) {
    return true;
  }

  // e.g. the highest level doesnt exist in the tree so in that case we couldn't find any ancestor up to the tree
  const parentIndex = tree.findIndex((x) => x.id === node.parent);
  if (parentIndex < 0) {
    return false;
  }

  if (isNodeAncestorOf(tree, ancestorId, node.parent)) {
    return true;
  } else {
    return false;
  }
}

/**
 * Recursively searching through the passed tree in order to find all ancestors of the passed in targetId and include every ancestor node into the result
 * @param tree Tree data that will be searched for ancestors
 * @param targetId The id of the node that will be included
 * @returns Tree (array of nodes) including all nodes that are direct & indirect parent of the target id
 */
function getAncestorsOf(tree: NodeModel<SpaceNodeData>[], targetId: string | number): NodeModel<SpaceNodeData>[] {
  const newTree: NodeModel<SpaceNodeData>[] = [];

  const node = tree.find((x) => x.id === targetId);

  if (node) {
    newTree.push(node);

    if (node.parent) {
      newTree.push(...getAncestorsOf(tree, node.parent));
    }
  }

  return newTree;
}

export const useSpaceTreeData = (spaceHierarchy: SpaceHierarchyReadModel | undefined) => {

  const [treeData, setTreeData] = useState<NodeModel<SpaceNodeData>[]>([]);

  const nodeIds = useMemo(() => treeData.map((x) => x.id), [treeData])

  /**
   * Will update the tree and correct the `data` property section
   */
  const updateTree = useCallback((updatedTree: NodeModel<SpaceNodeData>[]) => {
    const newTree = updateDataProperty(updatedTree);
    setTreeData(newTree);
    return newTree;
  }, []);

  /**
   * Will filter tree by the given parent id returning all the nodes that have the parent id matched
   */
  const filterTreeByParentId = useCallback((tree: NodeModel<SpaceNodeData>[], parent: string | number) => {
    return tree.filter((x) => x.parent === parent);
  }, [])

  /**
   * Will filter tree by the given child id returning all the nodes that are ancestor of this child excluding the child
   */
  const filterTreeByAncestorOfChildId = useCallback((tree: NodeModel<SpaceNodeData>[], child: string | number) => {
    return getAncestorsOf(tree, child).filter((x) => x.id !== child);
  }, [])

  useEffect(() => {
    if (spaceHierarchy == null) {
      setTreeData([]);
      return;
    };
    setTreeData(parseReadModelToNodeModel(spaceHierarchy));
  }, [setTreeData, spaceHierarchy]);

  return { treeData, updateTree, filterTreeByParentId, filterTreeByAncestorOfChildId, nodeIds, isNodeAncestorOf };
}
